
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAppDataContext } from '@/contexts/AppDataContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

const Departments = () => {
  const { data, loading } = useAppDataContext();
  const { user } = useAuth();
  const [activeView, setActiveView] = useState<'list' | 'stats'>('list');
  const [departmentList, setDepartmentList] = useState([]);
  const [newDepartment, setNewDepartment] = useState({ name: "", description: "" });
  
  // Extract unique departments and count employees in each
  const departmentStats = data.employees.reduce((acc, emp) => {
    if (emp.department_id) {
      acc[emp.department_id] = (acc[emp.department_id] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);
  
  const fetchDepartments = () => 
    apiService.getDepartments(user?.cid ?? null).then(resp => 
      setDepartmentList(Array.isArray(resp.data) ? resp.data : [])
    );

  useEffect(() => { fetchDepartments(); }, [user?.cid]);

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    await apiService.createDepartment({ ...newDepartment, cid: user?.cid ?? null });
    setNewDepartment({ name: "", description: "" });
    fetchDepartments();
  };

  const departments = Object.keys(departmentStats).sort();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold tracking-tight">Departments</h1>
          <div className="space-x-2">
            <Button 
              variant={activeView === 'list' ? 'default' : 'outline'} 
              onClick={() => setActiveView('list')}
            >
              List View
            </Button>
            <Button 
              variant={activeView === 'stats' ? 'default' : 'outline'} 
              onClick={() => setActiveView('stats')}
            >
              Statistics
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-payroll-primary"></div>
          </div>
        ) : (
          activeView === 'list' ? (
            <Card>
              <CardHeader>
                <CardTitle>Department List</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddDepartment} className="mb-4 flex gap-2">
                  <input
                    type="text"
                    placeholder="Department Name"
                    value={newDepartment.name}
                    onChange={e => setNewDepartment(d => ({ ...d, name: e.target.value }))}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Description"
                    value={newDepartment.description}
                    onChange={e => setNewDepartment(d => ({ ...d, description: e.target.value }))}
                  />
                  <button type="submit">Add Department</button>
                </form>
                <ul>
                  {departmentList.map(dep => (
                    <li key={dep.id}>{dep.name} - {dep.description}</li>
                  ))}
                </ul>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Department Name</TableHead>
                      <TableHead>Staff Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {departments.map(dept => (
                      <TableRow key={dept}>
                        <TableCell className="font-medium">{dept}</TableCell>
                        <TableCell>{departmentStats[dept]}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Department Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {departments.map(dept => (
                    <Card key={dept}>
                      <CardContent className="pt-6">
                        <h3 className="text-lg font-semibold">{dept}</h3>
                        <div className="mt-2 flex justify-between items-center">
                          <span className="text-sm text-gray-500">Total Staff:</span>
                          <span className="text-lg font-bold">{departmentStats[dept]}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )
        )}
      </div>
    </DashboardLayout>
  );
};

export default Departments;
